import { keyBy } from 'lodash'
import {
  HourSeriesFragment,
  PerformanceTableFragment,
  ProviderDemandFragment,
  ProviderFragment,
  ProviderParametersFragment,
  ProviderType,
  TemperatureCurveFragment,
} from '../../../generated/graphql'
import { groupBy, reduceToSumOfItems, sum } from '../../../util/array-utility'
import {
  createCombinedInputFlows,
  EnergyFlow,
  EnergyType,
  filterIncoming,
  filterOutgoing,
  isPrimaryEnergyFlow,
  PrimaryEnergyFlow,
  SecondaryEnergyFlow,
  Quadrant,
} from '../../EnergyFlow'
import {
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
  SecondaryHourlyEnergyFlow,
} from '../../HourlyEnergyFlow'

/**
 * Floats aren't accurate so we allow for a small error when comparing energy flows.
 */
export const balanceErrorThreshold = 0.5

export interface ProviderConstructor<
  TParameters extends ProviderParametersFragment = ProviderParametersFragment,
> {
  new (
    provider: ProviderFragment,
    temperatureValues?: number[],
  ): Provider<TParameters>
}

export type HourlyVariables = {
  outdoorTemperature?: number
}

/**
 * Base provider class, each provider/device should extend this class to
 * implement their calculation logic.
 */
export abstract class Provider<
  TParameters extends ProviderParametersFragment = ProviderParametersFragment,
  THourlyVariables extends HourlyVariables = HourlyVariables,
> {
  /**
   * All energy flow outputs from this provider.
   */
  public flows: EnergyFlow[] | null = null

  /**
   * All allocated input energy flows for this provider.
   */
  public inputFlows: PrimaryEnergyFlow[] | null = null

  /**
   * User defined parameters: scalars (e.g. max power)
   */
  public readonly parameters: TParameters

  /**
   * User defined parameters: hour series (e.g. solar panel density values)
   */
  protected readonly hourSeries: HourSeriesFragment[]

  /**
   * Hour series keyed by id to avoid unnecessary O(n) lookups.
   */
  protected readonly hourSeriesById: Record<string, HourSeriesFragment>

  /**
   * User defined parameters: performance tables (currently only for hybrid heat pumps).
   * Keyed by id to avoid unnecessary O(n) lookups.
   */
  protected readonly performanceTablesById: Record<
    string,
    PerformanceTableFragment
  >

  /**
   * User defined parameters: temperature curves (currently only for hybrid heat pumps).
   * Keyed by id to avoid unnecessary O(n) lookups.
   */
  protected readonly temperatureCurvesById: Record<
    string,
    TemperatureCurveFragment
  >

  /**
   * User defined demands to provide for. Keyed by demand id to avoid
   * unnecessary O(n) lookups as this happens often.
   */
  private readonly providerDemandsByDemandId: Record<
    string,
    ProviderDemandFragment
  >

  /**
   * Demands are filtered by energy type when selecting demands to provide for.
   */
  public abstract readonly providedEnergyTypes: EnergyType[]

  /**
   * @param provider Provider fragment from database.
   * @param temperatureValues Outdoor temperature values from database.
   */
  public constructor(
    private readonly provider: ProviderFragment,
    protected readonly temperatureValues?: number[],
  ) {
    this.parameters = provider.parameters as TParameters
    this.hourSeries = provider.hourSeries
    this.hourSeriesById = keyBy(
      provider.hourSeries,
      (hourSeries) => hourSeries.id,
    )
    this.performanceTablesById = keyBy(
      provider.performanceTables,
      (table) => table.id,
    )
    this.temperatureCurvesById = keyBy(
      provider.temperatureCurves,
      (curve) => curve.id,
    )
    this.providerDemandsByDemandId = keyBy(
      provider.demands,
      (providerDemand) => providerDemand.demand.id,
    )
  }

  public get id(): string {
    return this.provider.id
  }

  public get name(): string {
    return this.provider.name
  }

  public get type(): ProviderType {
    return this.provider.type
  }

  public get providesElectricityForProviders(): boolean {
    return this.provider.providesElectricityForProviders
  }

  public get isFallback(): boolean {
    return this.provider.isFallback
  }

  public get createdAt(): Date {
    return new Date(this.provider.createdAt)
  }

  /**
   * Provides for demands.
   */
  public provideForDemand(
    flows: EnergyFlow[],
    overproductions: SecondaryEnergyFlow[] = [],
  ): EnergyFlow[] {
    this.initialize()

    const inputEnergyFlows = this.applyAllocations(flows)

    const inputEnergyFlowsByEnergyType = groupBy(
      inputEnergyFlows,
      (flow) => flow.energyType,
    )

    const combinedInputEnergyFlows = createCombinedInputFlows(
      inputEnergyFlowsByEnergyType,
    )

    this.inputFlows = combinedInputEnergyFlows

    if (
      this.shouldTriggerEarlyReturn(combinedInputEnergyFlows, overproductions)
    ) {
      this.flows = []
      return this.flows
    }

    const outputEnergyFlows = this.provide(
      combinedInputEnergyFlows,
      overproductions,
    )

    const providedOutputEnergyFlows = outputEnergyFlows.filter(
      (flow) => flow.quadrant === Quadrant.Provided,
    )
    const otherOutputEnergyFlows = outputEnergyFlows.filter(
      (flow) => flow.quadrant !== Quadrant.Provided,
    )

    const splitProvidedOutputEnergyFlows = combinedInputEnergyFlows.flatMap(
      (inputFlow) => {
        const matchingOutputFlow = providedOutputEnergyFlows.find(
          (outputFlow) => outputFlow.energyType === inputFlow.energyType,
        )
        if (!matchingOutputFlow) {
          throw new Error(
            'Output flow energy types do not match input flow energy types',
          )
        }

        return this.splitOutputEnergyFlowEqually(
          inputEnergyFlowsByEnergyType[inputFlow.energyType],
          inputFlow,
          matchingOutputFlow,
        )
      },
    )

    this.flows = [
      ...splitProvidedOutputEnergyFlows,
      ...otherOutputEnergyFlows,
    ].filter((flow) => flow.hourSeries.length > 0)

    return this.flows
  }

  /**
   * Checks calculation balance and throws an error if something went wrong.
   */
  public checkBalance(): void {
    if (!this.flows) {
      throw new Error('Can not check balance before provider has provided')
    }

    const mapEnergyFlowToHourSeriesWithCoolingNegated = (
      flow: EnergyFlow,
    ): number[] =>
      flow.energyType === EnergyType.Cooling
        ? flow.hourSeries.map((value) => -value)
        : flow.hourSeries

    const incomingSeries = this.flows
      .filter(filterIncoming)
      .map(mapEnergyFlowToHourSeriesWithCoolingNegated)
      .reduce(reduceToSumOfItems, [])

    const outgoingSeries = this.flows
      .filter(filterOutgoing)
      .map(mapEnergyFlowToHourSeriesWithCoolingNegated)
      .reduce(reduceToSumOfItems, [])

    if (incomingSeries.length !== outgoingSeries.length) {
      throw new Error('Data length mismatch when checking provider balance')
    }

    incomingSeries.forEach((incoming, hour) => {
      if (Math.abs(incoming - outgoingSeries[hour]) > balanceErrorThreshold) {
        throw new Error(
          `Error in provider balance: in ${incoming}, out ${outgoingSeries[hour]} for hour ${hour}`,
        )
      }
    })
  }

  /**
   * Providers may implement this to initialize internal provider state
   * (e.g. if they provide from a limited resource).
   */
  protected initialize(): void {}

  /**
   * Commonly always trigger early return if there are no combined input energy flows.
   *
   * Override if necessary.
   */
  protected shouldTriggerEarlyReturn(
    combinedInputEnergyFlows: PrimaryEnergyFlow[],
    _overproductions?: SecondaryEnergyFlow[],
  ): boolean {
    return combinedInputEnergyFlows.length === 0
  }

  /**
   * Providers may override this to implement calculation logic that depends on other hours.
   */
  protected provide(
    demands: PrimaryEnergyFlow[],
    overproductions: SecondaryEnergyFlow[],
  ): EnergyFlow[] {
    if (demands.length === 0 && overproductions.length === 0) {
      return []
    }
    const hourIndices =
      demands.length > 0
        ? demands[0].hourSeries.map((_, hour) => hour)
        : overproductions[0].hourSeries.map((_, hour) => hour)

    if (hourIndices.length === 0) {
      return []
    }

    const hourlyOutputFlows = hourIndices.map((_, hour) =>
      this.provideForHour(
        this.getHourlyVariables(
          hour,
          demands.map((demand) => ({
            ...demand,
            value: demand.hourSeries[hour],
          })),
          overproductions.map((overproduction) => ({
            ...overproduction,
            value: overproduction.hourSeries[hour],
          })),
        ),
      ),
    )
    if (!hourlyOutputFlows.length) {
      return []
    }

    return this.combineHourlyEnergyFlowsToEnergyFlows(hourlyOutputFlows)
  }

  protected getHourlyVariables(
    hour: number,
    _demands: PrimaryHourlyEnergyFlow[],
    _overproductions: SecondaryHourlyEnergyFlow[],
  ): THourlyVariables {
    return {
      outdoorTemperature: this.temperatureValues?.[hour],
    } as THourlyVariables
  }

  /**
   * Combines hourly energy flows into standard energy flows.
   *
   * Providers may call this when implementing a custom provide method.
   */
  protected combineHourlyEnergyFlowsToEnergyFlows(
    hourlyOutputFlows: HourlyEnergyFlow[][],
  ): EnergyFlow[] {
    const outputFlowsForFirstHour = hourlyOutputFlows[0]

    return outputFlowsForFirstHour.map((hourlyFlow, index) => {
      const { value, ...baseFlow } = hourlyFlow

      return {
        ...baseFlow,
        hourSeries: hourlyOutputFlows.map((f) => f[index].value),
      }
    })
  }

  /**
   * Filters all input demands to a list of demands we can provide for.
   */
  protected applyAllocations(flows: EnergyFlow[]): EnergyFlow[] {
    return flows
      .filter((flow) => sum(flow.hourSeries) > 0)
      .map((demand) => {
        if (!isPrimaryEnergyFlow(demand)) {
          return demand
        }

        return {
          ...demand,
          hourSeries: demand.hourSeries.map(
            (value) => value * this.getAllocation(demand.demandId),
          ),
        }
      })
  }

  /**
   * Returns allocation as a portion.
   */
  private getAllocation(demandId: string): number {
    return this.providerDemandsByDemandId[demandId]?.maxPortion ?? 0
  }

  /**
   * Splits output energy flow equally to match multiple demands.
   */
  private splitOutputEnergyFlowEqually(
    inputEnergyFlows: EnergyFlow[],
    combinedInputEnergyFlow: EnergyFlow,
    providedOutputEnergyFlow: EnergyFlow,
  ): EnergyFlow[] {
    return inputEnergyFlows.map((flow) => ({
      ...flow,
      quadrant: Quadrant.Provided,
      hourSeries: flow.hourSeries.map((value, hour) =>
        combinedInputEnergyFlow.hourSeries[hour] > 0
          ? (value * providedOutputEnergyFlow.hourSeries[hour]) /
            combinedInputEnergyFlow.hourSeries[hour]
          : 0,
      ),
    }))
  }

  /**
   * Providers must implement their calculation logic here.
   */
  protected abstract provideForHour(
    variables: THourlyVariables,
  ): HourlyEnergyFlow[]
}
